import { useDispatch, useSelector } from 'react-redux';
import { useFlags, useNotifications } from '@fullstory/react-hooks';
import { VarRule } from '@fullstory/proto/fs/services/fieldmeta';
import { AppDispatch } from '../../types';
import { useEffect, useCallback } from 'react';
import { getOrgId } from '../fullstory/selectors';
import {
  getCustomEventProps,
  getSearchContext,
  getSearchContextWithoutDefinedEvents,
  getVisibleCustomEventProps,
} from './selectors';
import { getDefinedEventsAccess } from './definedEvents/selectors';
import { SearchMetaRequestActions } from './actions';
import { DefinedEventsRequestActions } from './definedEvents';
import { NamedElementsRequestActions } from '../namedElements';
import { WatchedElementAsyncActions } from '../watchedElements';
import { makeUseAccess } from '../../utils/accessControl';
import { PagesRequestActions } from '../pages';
import { APIEventIssueSilence } from '@fullstory/proto/fs/services/dx-objects';
import { AppBackendRequestActions } from '../appbackend';
import { intl } from '@fullstory/intl';
import { useFetchFsSpec, useFsSpecActions } from '../specs';
import { SpecTargetObjectType } from '@fullstory/proto/fs/services/search-settings';

/**
 * This hook will load the properties for the eventName provided.
 * @param eventName - custom event name to load properties for
 * @param includeHidden - if true, then hidden properties will be included
 */
export const useCustomEventProps = (eventName: string, includeHidden?: boolean) => {
  const customEventProps = useSelector(getVisibleCustomEventProps);
  const allCustomEventProps = useSelector(getCustomEventProps);
  return (
    (includeHidden ? allCustomEventProps.get(eventName) : customEventProps.get(eventName)) || []
  );
};

export interface EventSearchContextOptions {
  // If true, no defined events will be included in the search context. Mappings
  // for spec objects that _could_ be mapped to defined events, even if they
  // currently aren't, are also excluded. This is used for example in the
  // defined events editor to prevent recursive definitions.
  omitDefinedEvents?: boolean;
}

/**
 * This hook provides the necessary SearchContext for rendering the dynamic parts of the
 * the search UI.
 * @param options
 */
export const useEventSearchContext = (options: EventSearchContextOptions = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();

  const context = useSelector(getSearchContext);
  const contextWithoutDefinedEvents = useSelector(getSearchContextWithoutDefinedEvents);

  useFetchFsSpec();

  useEffect(() => {
    dispatch(SearchMetaRequestActions.getHiddenVars(orgId));
    dispatch(SearchMetaRequestActions.getCustomSchema(orgId, flags));
    if (!options.omitDefinedEvents) {
      dispatch(DefinedEventsRequestActions.getAllDefinedEventsRequest(orgId));
    }
    dispatch(NamedElementsRequestActions.getAllNamedElements(orgId));
    dispatch(WatchedElementAsyncActions.fetch());
    dispatch(PagesRequestActions.getAllPages(orgId));
  }, [dispatch, orgId, options.omitDefinedEvents, flags]);

  return options.omitDefinedEvents ? contextWithoutDefinedEvents : context;
};

export const useDefinedEventAccess = makeUseAccess(
  DefinedEventsRequestActions.getAccess,
  getDefinedEventsAccess,
);

export interface APIEventIssueSilenceUpdate {
  prev: APIEventIssueSilence | null;
  next: APIEventIssueSilence | null;
}

export const useAPIEventActions = () => {
  const notifications = useNotifications();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const { trackSpecMappingUpdate, updateObjectWithFsSpec } = useFsSpecActions();

  const saveEvent = useCallback(
    async (apiEventName: string, specSlugs: string[]) => {
      try {
        if (flags.featureFlags().AppFsSpec && apiEventName) {
          const mappings = await updateObjectWithFsSpec(
            apiEventName,
            SpecTargetObjectType.SPEC_TARGET_OBJECT_TYPE_API_EVENT,
            specSlugs,
          );
          mappings.forEach(({ mapping }) => {
            if (mapping?.mapping) trackSpecMappingUpdate(mapping.mapping, 'UPDATE', 'MODAL');
          });
        }
        notifications?.success('Successfully saved API Event');
      } catch (e) {
        notifications?.error('Failed to save API Event');
      }
    },
    [flags, updateObjectWithFsSpec, trackSpecMappingUpdate, notifications],
  );

  const archiveEvent = useCallback(
    async (apiEventName: string) => {
      try {
        const response = await dispatch(
          SearchMetaRequestActions.updateVarRule(orgId, {
            type: VarRule.VarType.VAR_TYPE_EVENT_NAME,
            pattern: apiEventName,
            parentPattern: '',
            state: VarRule.VarState.VAR_STATE_ARCHIVED,
            applyBefore: null,
          }),
        );
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Successfully archived API Event', id: 'X21/5H' }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to archive API Event property',
            id: 'pZf4Kx',
          }),
        );
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const unarchiveEvent = useCallback(
    async (apiEventName: string) => {
      try {
        const response = await dispatch(
          SearchMetaRequestActions.updateVarRule(orgId, {
            type: VarRule.VarType.VAR_TYPE_EVENT_NAME,
            pattern: apiEventName,
            parentPattern: '',
            state: VarRule.VarState.VAR_STATE_INVALID,
            applyBefore: null,
          }),
        );
        notifications.success(
          intl.formatMessage({ defaultMessage: 'Successfully unarchived API Event', id: 'jC6sB0' }),
        );
        return response;
      } catch (e) {
        notifications.error(
          intl.formatMessage({
            defaultMessage: 'Failed to unarchive API Event property',
            id: 'YHAZCR',
          }),
        );
        return null;
      }
    },
    [dispatch, notifications, orgId],
  );

  const snoozeEvent = useCallback(
    async (apiEventId: string, update: APIEventIssueSilenceUpdate) => {
      if (!update.prev && update.next) {
        try {
          const rsp = await dispatch(
            AppBackendRequestActions.snoozeApiEvent({
              orgId,
              apiEventId,
              expiresAt: update.next.expiresAt,
            }),
          );
          notifications.success(
            intl.formatMessage({ defaultMessage: 'Successfully snoozed issue.', id: '7/jrgx' }),
          );
          return rsp.issueSilence;
        } catch (e) {
          notifications.error(
            intl.formatMessage({ defaultMessage: 'Failed to snooze issue.', id: 'DEcYC9' }),
          );
          return null;
        }
      }

      if (update.prev && !update.next) {
        try {
          await dispatch(
            AppBackendRequestActions.unsnoozeApiEvent({
              orgId,
              apiEventId,
              issueSilenceId: update.prev.id,
            }),
          );
          notifications.success(
            intl.formatMessage({ defaultMessage: 'Successfully unsnoozed issue.', id: 'Apu5Re' }),
          );
        } catch (e) {
          notifications.error(
            intl.formatMessage({ defaultMessage: 'Failed to unsnooze issue.', id: 'jtEHlo' }),
          );
        }
      }

      return null;
    },
    [dispatch, notifications, orgId],
  );

  return { saveEvent, archiveEvent, unarchiveEvent, snoozeEvent };
};
